import {getBcp47LanguageTag} from '@aboutyou/config';
import {useLocale, useTranslation} from '@aboutyou/i18n';
import {useHydrationDone} from '@aboutyou/lazy-imports';
import {styled} from '@linaria/react';
import React, {useEffect, useState} from 'react';
import {COLORS} from '../../../constants/colors';
import {overflowEllipsis} from '../../../constants/ellipsis';
import {INSTALL_BANNER_HEIGHT} from '../../../constants/heights';
import {Button} from '../../atoms/Buttons/Button';
import {UnstyledButton} from '../../atoms/Buttons/UnstyledButton';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {StarRating} from '../../atoms/StarRating/StarRating';
import {Text} from '../../atoms/Text/Text';

export interface AppDetails {
  score?: number;
  numRatings?: number;
  title: string;
  iconUrl: string;
  storeUrl?: string;
}

export type InstallBannerProps = AppDetails & {
  onAppClick(): void;
  onClose(): void;
  isInstalled: boolean;
  fallbackLabel?: string;
  goToAppLabel?: string;
};

export function InstallBanner({
  iconUrl,
  title,
  numRatings,
  score,
  storeUrl,
  onAppClick,
  onClose,
  isInstalled,
  fallbackLabel,
  goToAppLabel,
}: InstallBannerProps) {
  const {t} = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const isPrimary = !isInstalled;
  const hydrationDone = useHydrationDone();

  useEffect(() => {
    if (isLoading && hydrationDone) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setIsLoading(false);
    }
  }, [isLoading, hydrationDone]);

  return (
    <Container>
      <UnstyledButton
        data-testid="installbanner-close"
        onClick={onClose}
        aria-label={t('Cancel')}
      >
        <Icon asset={GenericIconName.Close} size="S" fill={COLORS.Grey1} />
      </UnstyledButton>
      <AppIcon
        src={iconUrl}
        role="presentation"
        alt={undefined}
        loading="lazy"
      />
      <TextContainer>
        <Title weight="bold" size="px12">
          {title}
        </Title>
        <Subtitle weight="mediumBold" size="px10">
          <RatingsComponent
            fallbackLabel={fallbackLabel}
            numRatings={numRatings}
            score={score}
          />
        </Subtitle>
      </TextContainer>

      <InstallOrOpenButton
        aria-label={undefined}
        data-testid="installbanner-cta"
        href={storeUrl}
        onClick={onAppClick}
        variant={isPrimary ? 'primary' : 'secondary'}
        isLoading={isLoading}
        size="fit-width"
      >
        {isInstalled ? t('Open') : goToAppLabel}
      </InstallOrOpenButton>
    </Container>
  );
}

function RatingsComponent(
  props: Pick<InstallBannerProps, 'numRatings' | 'score' | 'fallbackLabel'>,
) {
  const {numRatings, score} = props;
  const locale = getBcp47LanguageTag(useLocale());

  return (
    <>
      {score && <StarRating value={score} />}
      {numRatings && `(${numRatings.toLocaleString(locale)})`}
    </>
  );
}

const Container = styled.div`
  box-sizing: border-box;
  display: flex;
  width: 100%;
  height: ${INSTALL_BANNER_HEIGHT}px;
  border-bottom: 1px solid ${COLORS.Grey5};
  background-color: ${COLORS.White};
  align-items: center;
  padding: 12px 16px;
`;

const TextContainer = styled('div')`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  line-height: 1.75;
  min-height: 17.5px;
  ${overflowEllipsis}
`;

const AppIcon = styled('img')`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background-color: ${COLORS.Black};
  margin-left: 10px;
  margin-right: 10px;
`;

const Title = styled(Text)`
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
  color: ${COLORS.Black};
  ${overflowEllipsis}
`;

const Subtitle = styled(Text)`
  font-stretch: normal;
  font-style: normal;
  line-height: 1.75;
  min-height: 17.5px;
  letter-spacing: normal;
  color: ${COLORS.Black};
  display: inline-flex;
  align-items: center;
  ${overflowEllipsis}
`;

const InstallOrOpenButton = styled(Button)`
  margin: 0 12px 0 0;
  white-space: nowrap;
`;
